import { useCallback, useEffect, useRef } from 'react'
import { useT } from '@transifex/react'
import {
  stopPerformanceMeasure,
  submitMonoliphForm,
  trackUserEvent,
} from '@vend/utilities'
import { ToastNotifications } from '@lightspeed/design-system-react'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import {
  finalisingSale,
  finalisingSaleComplete,
} from '../../../state/slices/uiStateSlice'
import {
  usePaymentManager,
  useRegisterManager,
  useRetailerSettingsManager,
  useSaleManager,
  useTrainingModeManager,
  useUserManager,
  useUserSwitchingService,
} from '../../../ng-compat/hooks'
import {
  ReduxCurrentSale,
  selectCurrentSale,
  selectLineItems,
  selectCustomer,
  selectSaleFinals,
  selectSaleId,
  selectSaleSummaryFields,
  selectPayAccountBalance,
  selectCustomerBalances,
  updateCustomerBalances,
  addPaymentRequestSaleAttribute,
  setSaleCompletedBy,
} from '../../../state/slices/currentSale/currentSaleSlice'
import {
  isMarkedAsUnfulfilled,
  isQuote,
  isUploadedToBeCompleted,
} from '../../../functions/sale'
import { WEB_REGISTER } from '../../web-register.constant'
import { SaleStatus, SaleSyncStatus } from '../../../models/sale.constants'
import { STATES } from '../../../../../lib/routes'
import { SaleManager } from '../../../managers/sale-manager'
import { $state } from '../../../ng-compat/imports'
import { LineItem } from '../../../models/line-item'
import { PaymentManager } from '../../../managers/payment-manager'
import {
  clearPaymentRequestSuccess,
  selectTaxInformation,
  setPaymentRequestSuccess,
} from '../../../state/slices/posState/posStateSlice'
import { useSpecialOrderProduct } from '../../special-order-product/useSpecialOrderProduct'
import { useSpecialOrderProductToast } from '../../special-order-product/useSpecialOrderProductToast'
import { store } from '../../../state/store'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { useSpecialOrderUBE } from '../../special-order-product/useSpecialOrderUBE'
import { client } from '../../../apollo'
import { FEATURE_COMPLETE_PICKUP_AFTER_PAYMENT } from '../../../constants'
import { FEATURE_ON_ACCOUNT_PAY_VIA_REGISTER } from '../../../constants/wr-features'
import {
  getAndClearPostPaymentRedirectUrl,
  peekPostPaymentRedirectUrl,
  clearPostPaymentRedirectUrl,
} from '../../../util/post-payment-redirect'
import { FulfilSaleMutation } from '../../../queries/fulfil-sale'
import { saveSaleAndCreatePaymentRequest } from '../../../functions/invoicing/save-sale-and-create-payment-request'
import {
  buildPaymentRequestDto,
  buildRetailerDetails,
} from '../../../functions/invoicing/payment-request-resources'
import { useFetchPaymentTypes } from '../payment-panel/hooks/useFetchPaymentTypes'
import { QuotePaymentRequestState } from './QuotePaymentRequestCard'
import { usePrintReceipt } from './usePrintReceipt'

const trackAccountSaleEvent = ({
  sale,
  lineItems,
}: {
  sale: ReduxCurrentSale
  lineItems: LineItem[]
}) => {
  trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Close Sale Confirmation', {
    id: sale.localId,
  })

  if (
    sale.status === SaleStatus.ACCOUNT_SALE ||
    sale.status === SaleStatus.ACCOUNT_SALE_CLOSED
  ) {
    const existingLineItemCount = lineItems.filter(lineItem =>
      Boolean(lineItem.id)
    ).length

    if (existingLineItemCount > 0 && existingLineItemCount < lineItems.length) {
      trackUserEvent(
        WEB_REGISTER.APPLICATION_NAME,
        'Added additional product(s) to on-account sale'
      )
    }
  }
}

const getPayments = (sale: ReduxCurrentSale) =>
  sale.payments.map(p => {
    return {
      paymentTypeName: p.paymentType.name,
      paymentTypeId: p.paymentType.type_id,
      paymentDate: p.paymentDate,
      retailerPaymentTypeId: p.paymentType.id,
      salePaymentId: p.localId,
    }
  })

const clearPaymentMessages = (paymentManager: PaymentManager) => {
  paymentManager.paymentIntegrationReceiptMessage = null
  paymentManager.acceptedPaymentIntegrationReceiptMessages = []
}

// Timeout for waiting for early submit to complete (in milliseconds).
// If the network is down, we don't want to block forever - fall back to local invoice number.
const EARLY_SUBMIT_TIMEOUT_MS = 5000

// Timeout for waiting for receipt templates to load (in milliseconds).
const RECEIPT_LOAD_TIMEOUT_MS = 5000

/**
 * Wait for the early submit sale to complete, with a timeout.
 * This ensures the server-returned invoice number is available before printing,
 * but doesn't block indefinitely if the network is down.
 *
 * @returns true if the early submit completed, false if it timed out
 */
const waitForEarlySubmitComplete = (
  saleManager: SaleManager
): Promise<boolean> =>
  new Promise(resolve => {
    const currentSyncStatus = selectCurrentSale(store.getState()).syncStatus
    if (currentSyncStatus !== SaleSyncStatus.UPLOAD_FIRST_SUBMIT) {
      resolve(true) // Already completed
      return
    }

    const timeoutId = setTimeout(() => {
      saleManager.off('earlySubmitSaleComplete', handler)
      resolve(false) // Timed out
    }, EARLY_SUBMIT_TIMEOUT_MS)

    const handler = () => {
      clearTimeout(timeoutId)
      saleManager.off('earlySubmitSaleComplete', handler)
      resolve(true) // Completed successfully
    }

    saleManager.on('earlySubmitSaleComplete', handler)
  })

/**
 * Wait for receipt templates to load before sending email.
 * This prevents the race condition where email fails because receiptID is not yet available.
 *
 * @returns true if receiptID is available, false if it timed out
 */
const waitForReceiptTemplates = (): Promise<boolean> =>
  new Promise(resolve => {
    const checkReceipt = () => {
      const currentSale = selectCurrentSale(store.getState())
      return !!(currentSale.receiptID && currentSale.receiptEmailAddress)
    }

    // Check immediately - receipts might already be loaded
    if (checkReceipt()) {
      resolve(true)
      return
    }

    const startTime = Date.now()
    const pollInterval = 100 // Check every 100ms

    const intervalId = setInterval(() => {
      if (checkReceipt()) {
        clearInterval(intervalId)
        resolve(true)
      } else if (Date.now() - startTime >= RECEIPT_LOAD_TIMEOUT_MS) {
        clearInterval(intervalId)
        resolve(false)
      }
    }, pollInterval)
  })

export const useCloseSaleConfirmation = (
  markedAsPickedUp: boolean,
  setIsPickedUp: (value: boolean) => void,
  quotePaymentRequest?: QuotePaymentRequestState,
  onShareLinkReady?: (proposalUrl: string) => Promise<void>
) => {
  const t = useT()
  const dispatch = useAppDispatch()
  const sale = useAppSelector(selectCurrentSale)
  const lineItems = useAppSelector(selectLineItems)
  const customer = useAppSelector(selectCustomer)
  const saleFinals = useAppSelector(selectSaleFinals)
  const saleIdFromStore = useAppSelector(selectSaleId)
  const { discount } = useAppSelector(selectSaleSummaryFields)
  const payAccountBalance = useAppSelector(selectPayAccountBalance)
  const customerBalances = useAppSelector(selectCustomerBalances)
  const saleManager = useSaleManager()
  const paymentManager = usePaymentManager()
  const trainingModeManager = useTrainingModeManager()
  const registerManager = useRegisterManager()
  const register = registerManager.getActiveRegister()
  const currentOutlet = registerManager.getActiveOutlet()
  const userSwitchingService = useUserSwitchingService()
  const printReceipt = usePrintReceipt()
  const isSpecialOrderProduct = useSpecialOrderProduct()
  const specialOrderProductToast = useSpecialOrderProductToast()
  const trackSpecialOrderEvent = useSpecialOrderUBE()
  const retailer = useRetailerSettingsManager().get()
  const taxInformation = useAppSelector(selectTaxInformation)
  const userManager = useUserManager()
  const user = userManager.getLoggedInUser()
  const { systemPaymentTypes } = useFetchPaymentTypes({
    enabled: !!quotePaymentRequest,
  })

  // Feature flag to enable waiting for receipt templates (defaults to OFF).
  // Only enable for merchants experiencing the race condition where receiptID is
  // not available in time for email sending (particularly for payment link sales).
  const shouldWaitForReceiptTemplates = getFeatureFromCache(
    'wait_for_receipt_templates'
  )

  const markedAsPickedUpRef = useRef(markedAsPickedUp)
  useEffect(() => {
    markedAsPickedUpRef.current = markedAsPickedUp
  }, [markedAsPickedUp])

  // Synchronous guard against concurrent invocations. The Redux-driven
  // `isFinalising` → `disabled` prop update requires a React re-render, leaving
  // a brief window where a rapid double-click can fire the handler twice before
  // the button is visually disabled. The ref update is immediate and prevents
  // any re-entrant call regardless of render timing.
  const isCompletingRef = useRef(false)

  const closeSaleConfirmation = useCallback(
    async (source: string) => {
      if (isCompletingRef.current) {
        return
      }
      isCompletingRef.current = true
      try {
        dispatch(finalisingSale())

        trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Close Sale Confirmation', {
          id: sale.localId,
        })

        if (
          sale.status === SaleStatus.ACCOUNT_SALE ||
          sale.status === SaleStatus.ACCOUNT_SALE_CLOSED
        ) {
          trackAccountSaleEvent({ sale, lineItems })
        }

        const tempPayments = getPayments(sale)

        dispatch(setSaleCompletedBy(source))

        if (isMarkedAsUnfulfilled(sale.status)) {
          await saleManager.setPickableState()
        }

        // If the sale is still being uploaded for the first time, wait for it to complete (with timeout).
        // This ensures the server-returned invoice number is available for printing.
        // If the network is down, we proceed after the timeout with the local invoice number.
        await waitForEarlySubmitComplete(saleManager)

        // Re-read sale state after potential early submit completion to get the updated syncStatus.
        // The original `sale` variable may be stale if we waited for the early submit to complete.
        let currentSale = selectCurrentSale(store.getState())

        // If email receipt is configured and receiptID is not available, check if we need to wait.
        // The wait logic is feature-flagged (OFF by default) to avoid blocking sale completion
        // for merchants who don't experience the receipt template race condition.
        if (
          shouldWaitForReceiptTemplates &&
          register.email_receipt &&
          currentSale.receiptEmailAddress &&
          !currentSale.receiptID
        ) {
          // Feature flag enabled: wait for receipt templates to load
          const receiptsLoaded = await waitForReceiptTemplates()

          if (!receiptsLoaded) {
            // Timeout occurred - receipts didn't load in time
            // Email won't be sent automatically, but user can resend from sale history
            trackUserEvent(
              WEB_REGISTER.DISPLAY_NAME,
              'Receipt templates timeout',
              {
                saleId: currentSale.localId,
                timeout: RECEIPT_LOAD_TIMEOUT_MS,
              }
            )
          }

          // Re-read state after waiting for receipts
          currentSale = selectCurrentSale(store.getState())
        }

        // If this is a quote with payment request enabled, send the payment request before finalizing
        const isQuoteWithPaymentRequest =
          isQuote(sale.status) &&
          quotePaymentRequest?.isToggleEnabled &&
          Boolean(quotePaymentRequest?.amount) &&
          Boolean(
            quotePaymentRequest?.email || quotePaymentRequest?.isShareLink
          )
        if (isQuoteWithPaymentRequest) {
          dispatch(addPaymentRequestSaleAttribute())

          // The vend route expects first_name/last_name on the address payload
          // so the proposal/invoice can render the recipient name. Coalesce to
          // empty strings when the customer or fields are null/undefined to
          // avoid sending nulls to the API.
          const customerNameFields = {
            first_name: customer?.first_name || '',
            last_name: customer?.last_name || '',
          }

          const deliveryAddress = quotePaymentRequest.deliveryAddressDetails
            ? JSON.stringify({
                ...quotePaymentRequest.deliveryAddressDetails,
                ...customerNameFields,
              })
            : undefined

          const billingAddress = quotePaymentRequest.billingAddressDetails
            ? JSON.stringify({
                ...quotePaymentRequest.billingAddressDetails,
                ...customerNameFields,
              })
            : undefined

          const paymentRequestDto = buildPaymentRequestDto({
            customer,
            lineItems,
            saleIdFromStore,
            saleFinals,
            currentOutlet,
            currentRegister: register,
            retailer,
            discount,
            amountTendered: quotePaymentRequest.amount,
            user,
            lspayPaymentTypeId: systemPaymentTypes.lspayPaymentType?.id,
            taxInformation,
            deliveryAddress,
            billingAddress,
            fulfilmentType: quotePaymentRequest.convertTo,
          })

          // Override email and note from the card.
          // Share-link and email can both be enabled; when email is present we still
          // pass it through so the request can be emailed while the proposalUrl is
          // used to open the copy-link modal.
          paymentRequestDto.sendToEmail = quotePaymentRequest.email
          paymentRequestDto.note = quotePaymentRequest.note

          const retailerDetails = buildRetailerDetails({ retailer })
          const paymentRequestResult = await saveSaleAndCreatePaymentRequest(
            saleManager,
            paymentRequestDto,
            retailerDetails
          )

          if (paymentRequestResult.success) {
            dispatch(
              setPaymentRequestSuccess({
                paymentRequestId: paymentRequestResult.paymentRequestId!,
                requestedAmount:
                  paymentRequestResult.requestedAmount ||
                  paymentRequestDto.requestedAmount,
              })
            )

            if (
              quotePaymentRequest.isShareLink &&
              paymentRequestResult.proposalUrl
            ) {
              // Open the copy-link modal and wait for the merchant to close it before navigating.
              await onShareLinkReady?.(paymentRequestResult.proposalUrl)
            } else {
              const displayName =
                `${paymentRequestDto.customer.firstName} ${paymentRequestDto.customer.lastName}`.trim() ||
                quotePaymentRequest!.email
              ToastNotifications.success(
                t(`Payment request successfully sent to ${displayName}`, {
                  _key: 'payment-request-modal.create.success',
                  _context: 'payment-request-modal, toast',
                })
              )
            }
          }
        }

        // Sale-less ABP: no sale was created — skip finalising. The receipt is
        // printed from within the ABP block below after the balance deduction succeeds.
        if (
          !payAccountBalance?.abpTransactionId &&
          !isQuoteWithPaymentRequest
        ) {
          // Finalize the sale but skip the automatic newSale() call so we can print with full sale data
          if (isUploadedToBeCompleted(currentSale)) {
            await saleManager.completeSale({ skipNewSale: true })
          } else {
            await saleManager.finaliseSale({ skipNewSale: true })
          }

          // Print the receipt while sale data is still available
          const shouldPrint = register.print_receipt && !isQuote(sale.status)
          if (shouldPrint) {
            await printReceipt()
          }
        }

        // Mark the sale as picked up if the toggle is on and the feature flag is enabled.
        // Double-gated: feature flag AND toggle state must both be true.
        const completePickupAfterPaymentEnabled = getFeatureFromCache(
          FEATURE_COMPLETE_PICKUP_AFTER_PAYMENT
        )
        let pickupFulfilmentFailed = false

        if (
          completePickupAfterPaymentEnabled &&
          markedAsPickedUpRef.current &&
          Boolean(sale.id)
        ) {
          trackUserEvent(
            WEB_REGISTER.DISPLAY_NAME,
            'Pickup Fulfillment Requested',
            {
              saleId: sale.id,
              status: sale.status,
              fulfilmentType: 'PICK_UP',
            }
          )

          try {
            await client.mutate({
              mutation: FulfilSaleMutation,
              variables: {
                saleId: sale.id,
                type: 'PICK_UP',
              },
            })
          } catch (error) {
            pickupFulfilmentFailed = true
            trackUserEvent(
              WEB_REGISTER.DISPLAY_NAME,
              'Pickup Fulfillment Failed',
              {
                saleId: sale.id,
                error: error instanceof Error ? error.message : String(error),
              }
            )
          }
        } else if (
          completePickupAfterPaymentEnabled &&
          !markedAsPickedUpRef.current &&
          Boolean(sale.id)
        ) {
          trackUserEvent(
            WEB_REGISTER.DISPLAY_NAME,
            'Pickup Toggle Off Complete Sale',
            {
              saleId: sale.id,
              status: sale.status,
            }
          )
        }

        // Always reset the toggle state so it never leaks into the next sale,
        // regardless of feature flag or sale.id conditions.
        setIsPickedUp(false)

        // line items that are special order products
        const specialOrderProductLineItems = lineItems.filter(lineItem =>
          isSpecialOrderProduct(lineItem)
        )
        const numOfSpecialOrderProducts = specialOrderProductLineItems.length

        /**
         * Send UBE event for special order when not in training mode
         */
        if (!trainingModeManager.trainingModeEnabled) {
          trackSpecialOrderEvent()
        }

        // If we arrived via the account balance payment flow (/pay-account-balance route),
        // handle the balance deduction BEFORE clearing the sale so Redux state
        // (customer, payAccountBalance, customerBalances) is still available.
        if (
          payAccountBalance?.abpTransactionId &&
          getFeatureFromCache(FEATURE_ON_ACCOUNT_PAY_VIA_REGISTER)
        ) {
          // Peek (don't consume) the return URL — only clear it after a
          // successful balance deduction so that a retry of "Done" after a
          // transient failure still enters this block.
          const returnUrl = peekPostPaymentRedirectUrl()

          // Sale-less ABP flow: no sale was created so _handlePayAccountBalance
          // never fires. Call POST /customer/pay/:id directly here instead.
          if (customer?.id) {
            try {
              await submitMonoliphForm(
                `/customer/pay/${customer.id}`,
                'vend_customer_balance',
                {
                  '[amount]': String(payAccountBalance.amount),
                  '[payment_type_id]': payAccountBalance.paymentTypeId ?? '',
                  '[register_id]': register?.id ?? '',
                }
              )
              // Reduce the displayed account balance so the customers page
              // reflects the payment. Only update on success — if the POST
              // failed the balance was not deducted server-side.
              dispatch(
                updateCustomerBalances({
                  account:
                    (customerBalances?.account ?? 0) +
                    Number(payAccountBalance.amount),
                })
              )
              trackUserEvent(
                WEB_REGISTER.DISPLAY_NAME,
                'Account Balance Payment Completed',
                {
                  customerId: customer.id,
                  amount: payAccountBalance.amount,
                  abpTransactionId: payAccountBalance.abpTransactionId,
                }
              )
            } catch {
              // Payment was captured by LSPay but balance deduction failed.
              // Do NOT clear the sale or redirect — stay on the confirmation
              // screen so the merchant sees this error and can retry from the
              // Customers page.
              ToastNotifications.negative(
                t(
                  'Payment was taken but account balance could not be updated. Please retry from the Customers page.',
                  {
                    _context:
                      'Error when the monolith balance deduction call fails after a successful terminal payment in the account balance flow',
                  }
                )
              )
              dispatch(finalisingSaleComplete())
              return
            }
          }

          // Balance deduction succeeded (or no customer ID) — now clear the sale
          // and consume the return URL.

          // Print the receipt before clearing sale state, matching normal sale flow.
          if (register.print_receipt) {
            await printReceipt()
          }

          clearPostPaymentRedirectUrl()
          await saleManager.clearSale()
          clearPaymentMessages(paymentManager)
          dispatch(clearPaymentRequestSuccess())

          if (returnUrl) {
            // Intentionally skip userSwitchingService.switchUserIfNeeded: we are
            // leaving webregister entirely, so there is no "next sale" to prompt
            // for. The normal PIN-per-sale flow applies the next time webregister
            // is opened and a sale is completed.
            dispatch(finalisingSaleComplete())
            window.location.assign(returnUrl)
            return
          }

          // No return URL — fall through to the normal sell screen transition.
          $state.go(STATES.SALE, {}, { location: 'replace' })
          dispatch(finalisingSaleComplete())
          return
        }

        // Now clear the sale and start fresh.
        // Normal sales keep the synchronous newSale() path to avoid any
        // behavioural change.
        saleManager.newSale()

        clearPaymentMessages(paymentManager)

        // Clear payment request success flag when leaving confirmation screen
        dispatch(clearPaymentRequestSuccess())

        // If we arrived via the account balance payment flow (?action=pay_account_balance),
        // redirect back to the originating page (e.g. /customers) instead of the sell screen.
        if (getFeatureFromCache(FEATURE_ON_ACCOUNT_PAY_VIA_REGISTER)) {
          const returnUrl = getAndClearPostPaymentRedirectUrl()
          if (returnUrl) {
            // Intentionally skip userSwitchingService.switchUserIfNeeded: we are
            // leaving webregister entirely, so there is no "next sale" to prompt
            // for. The normal PIN-per-sale flow applies the next time webregister
            // is opened and a sale is completed.
            dispatch(finalisingSaleComplete())
            window.location.assign(returnUrl)
            return
          }
        }

        $state.go(STATES.SALE, {}, { location: 'replace' })

        if (!trainingModeManager.trainingModeEnabled) {
          stopPerformanceMeasure('sale-process', {
            moduleName: 'Web Register',
            metadata: { payments: tempPayments },
          })
        }

        const shouldTaintSession = !isQuote(sale.status)
        await userSwitchingService.switchUserIfNeeded(shouldTaintSession)

        dispatch(finalisingSaleComplete())

        // show the success toast for special order product when sale is completed
        specialOrderProductToast(numOfSpecialOrderProducts)

        if (pickupFulfilmentFailed) {
          ToastNotifications.negative(
            t(
              `Couldn't mark items as picked up. Try again from the "Fulfillments" page.`,
              {
                _context:
                  'Error toast when marking a pickup sale as fulfilled fails',
              }
            )
          )
        }
      } finally {
        isCompletingRef.current = false
      }
    },
    [
      dispatch,
      lineItems,
      paymentManager,
      printReceipt,
      register,
      sale,
      saleManager,
      trainingModeManager.trainingModeEnabled,
      userSwitchingService,
      isSpecialOrderProduct,
      specialOrderProductToast,
      trackSpecialOrderEvent,
      t,
      setIsPickedUp,
      quotePaymentRequest,
      customer,
      saleFinals,
      saleIdFromStore,
      currentOutlet,
      retailer,
      discount,
      user,
      systemPaymentTypes,
      taxInformation,
      onShareLinkReady,
      payAccountBalance,
      customerBalances,
      shouldWaitForReceiptTemplates,
    ]
  )

  return closeSaleConfirmation
}
